/**
 * TrialBanner — the ONLY tell that an org is a trial (04 §4.4). Slim persistent
 * bar rendered by the app shell whenever the active org has `trial: true`.
 * Three copy variants (sample / blank / frozen) with the lifecycle doors:
 * Keep (→ graduation), Erase / Start fresh (→ erase-and-recreate), and Extend
 * (blank trials with extensions remaining). Day-N is computed from stored
 * timestamps via trialLifecycle.deriveTrialState.
 */
import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import * as Sentry from "@sentry/react";
import UserContext from "../../assets/user_context";
import { useAuth } from "../../services/use-auth";
import { tracedCallable } from "../../utilities/tracedCallable";
import { deriveTrialState, TRIAL_CONSTANTS, SEED_CALL_OPTS } from "./trialLifecycle";
import { enterTrialOrg } from "./trialNav";
import { showEmailPreview } from "./emailPreviewStore";
import GraduationWizard from "./GraduationWizard";

const fmtDate = (d) =>
  d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";

// Restart-size options (mirrors TrialFork's SIZES).
const RESTART_SIZES = [
  { key: "small", label: "Small", hint: "3 funds, 3 registers" },
  { key: "medium", label: "Medium", hint: "thrift + payroll" },
  { key: "large", label: "Large", hint: "grants, 8 registers" },
];

export default function TrialBanner() {
  const { orgData, org } = useContext(UserContext);
  const { functions, db } = useAuth();
  const orgId = org?.id || org;

  const [busy, setBusy] = useState(false);
  const [showErase, setShowErase] = useState(false);
  const [nextSize, setNextSize] = useState(null);
  const [showGraduation, setShowGraduation] = useState(false);
  const [toast, setToast] = useState(null);
  const [previews, setPreviews] = useState([]);

  const isTrial = orgData?.trial === true;

  const state = useMemo(
    () => (isTrial ? deriveTrialState(orgData) : null),
    [isTrial, orgData],
  );

  // "N previews ready" (04 §4.8 async path): watch the org's preview subcollection.
  useEffect(() => {
    if (!isTrial || !db || !orgId) {
      setPreviews([]);
      return undefined;
    }
    const q = query(
      collection(db, `orgs/${orgId}/emailPreviews`),
      orderBy("createdAt", "desc"),
    );
    const unsub = onSnapshot(
      q,
      (snap) => setPreviews(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setPreviews([]),
    );
    return () => unsub();
  }, [isTrial, db, orgId]);

  if (!isTrial || !state) return null;

  const { flavor, frozen, dayN, totalDays, canExtend, canPromote, purgeAt } = state;

  const erase = async () => {
    setBusy(true);
    try {
      const call = tracedCallable(functions, "eraseTrialOrg", SEED_CALL_OPTS);
      const size = flavor === "sample" ? nextSize || orgData?.trialSize : undefined;
      const res = await call({ orgId, recreate: true, ...(size ? { size } : {}) });
      const freshId = (res?.data ?? res)?.orgId;
      if (freshId) enterTrialOrg(freshId);
      else setToast("Couldn't start fresh — please try again.");
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setTag("component", "TrialBanner");
        scope.setTag("action", "erase");
        scope.setExtra("orgId", orgId);
        Sentry.captureException(e);
      });
      setToast("Couldn't start fresh — please try again.");
    } finally {
      setBusy(false);
      setShowErase(false);
    }
  };

  const extend = async () => {
    setBusy(true);
    try {
      const call = tracedCallable(functions, "extendTrialOrg");
      const res = await call({ orgId });
      const days = (res?.data ?? res)?.extendedDays;
      setToast(
        days
          ? `Trial extended by ${TRIAL_CONSTANTS.EXTENSION_DAYS} days.`
          : "Trial extended.",
      );
    } catch (e) {
      if (e?.message?.includes("max_extensions_reached")) {
        setToast("You've used all your trial extensions.");
      } else {
        Sentry.withScope((scope) => {
          scope.setTag("component", "TrialBanner");
          scope.setTag("action", "extend");
          scope.setExtra("orgId", orgId);
          Sentry.captureException(e);
        });
        setToast("Couldn't extend the trial — please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const openLatestPreview = () => {
    if (previews[0]) showEmailPreview({ previewId: previews[0].id, orgId });
  };

  // Variant copy.
  const sampleOrgName = orgData.orgName || "Riverbend Food Pantry";
  let message;
  if (frozen) {
    message = (
      <>
        Trial ended — books are read-only.
        {purgeAt ? ` Purges on ${fmtDate(purgeAt)}.` : ""}
      </>
    );
  } else if (flavor === "sample" && orgData.trialSeedStatus === "seeding") {
    // Manual-navigation window while the seed is still writing (D4): tell the
    // user the books aren't complete yet instead of letting sparse screens
    // read as broken.
    message = (
      <>
        Still building <strong>{sampleOrgName}</strong>&apos;s sample books — some screens may
        look sparse for a few more seconds.
      </>
    );
  } else if (flavor === "sample") {
    message = (
      <>
        You&apos;re exploring <strong>{sampleOrgName}</strong> — sample books.
      </>
    );
  } else {
    message = (
      <>
        Trial books — day {dayN} of {totalDays}.
      </>
    );
  }

  return (
    <>
      <Box
        data-testid="trial-banner"
        data-trial-flavor={flavor}
        data-trial-frozen={frozen ? "true" : "false"}
        role="status"
        aria-label="Trial status"
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          px: 2,
          py: 0.75,
          fontSize: "0.875rem",
          bgcolor: frozen ? "warning.light" : "info.light",
          color: frozen ? "warning.contrastText" : "info.contrastText",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>{message}</Box>

        {previews.length > 0 && (
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={openLatestPreview}
            data-testid="trial-previews-ready">
            {previews.length} email preview{previews.length > 1 ? "s" : ""} ready
          </Button>
        )}

        {canPromote && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={busy}
            onClick={() => setShowGraduation(true)}
            data-testid="trial-keep">
            Keep these books
          </Button>
        )}

        {canExtend && (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            disabled={busy}
            onClick={extend}
            data-testid="trial-extend">
            Extend trial
          </Button>
        )}

        <Button
          size="small"
          variant="outlined"
          color="inherit"
          disabled={busy}
          onClick={() => {
            setNextSize(orgData?.trialSize || "medium");
            setShowErase(true);
          }}
          data-testid={flavor === "sample" ? "trial-start-fresh" : "trial-erase"}>
          {flavor === "sample" ? "Start fresh" : "Erase"}
        </Button>
      </Box>

      <Dialog open={showErase} onClose={busy ? undefined : () => setShowErase(false)} data-testid="trial-erase-confirm">
        <DialogTitle>{flavor === "sample" ? "Start fresh?" : "Erase these books?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes this trial org and{" "}
            {flavor === "sample" ? "reloads a fresh sample" : "starts a new blank trial"}. This
            can&apos;t be undone.
          </DialogContentText>
          {flavor === "sample" && (
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={nextSize}
              onChange={(e, v) => v && setNextSize(v)}
              sx={{ mt: 2 }}
              aria-label="Sample size for the fresh books"
              data-testid="trial-restart-size">
              {RESTART_SIZES.map((s) => (
                <ToggleButton
                  key={s.key}
                  value={s.key}
                  data-testid={`trial-restart-size-${s.key}`}
                  aria-label={`${s.label} sample`}
                  sx={{ textTransform: "none", flexDirection: "column", py: 0.75 }}>
                  <Typography variant="subtitle2">{s.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.hint}
                  </Typography>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErase(false)} disabled={busy} data-testid="trial-erase-cancel">
            Cancel
          </Button>
          <Button onClick={erase} color="error" variant="contained" disabled={busy} data-testid="trial-erase-confirm-btn">
            {busy ? <CircularProgress size={18} /> : flavor === "sample" ? "Start fresh" : "Erase"}
          </Button>
        </DialogActions>
      </Dialog>

      <GraduationWizard open={showGraduation} onClose={() => setShowGraduation(false)} orgId={orgId} />

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        data-testid="trial-banner-toast"
      />
    </>
  );
}
